import { useEffect, useMemo } from 'react'

import { Code } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'

import { Alert } from '@enechain/ecloud-designsystem'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { RegionLoading } from '~/components/common/RegionLoading'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { todayCivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import {
  convertBgMasterListItems,
  convertListBgMastersResponse,
  useListBgMasters,
} from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { DemandProcurementPlanMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/components/DemandProcurementPlanMasterFormFields'
import { useGetDemandProcurementMaster } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/hooks/useGetDemandProcurementMaster'
import { useGetDemandProcurementPlanMasterFormOptions } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/hooks/useGetDemandProcurementPlanMasterFormOptions'
import { useUpdateDemandProcurementMaster } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/hooks/useUpdateDemandProcurementMaster'
import {
  type DemandProcurementMasterFormValues,
  demandProcurementMasterFormSchema,
} from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/models/schema'
import { resolveDemandProcurementMasterDetailedError } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/models/serverError'
import type { DemandProcurementMasterRow } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/types'
import {
  convertListSubmitterMastersResponse,
  useListSubmitterMasters,
} from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import { useListTradingPartners } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/hooks/useListTradingPartners'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'

type Props = {
  isOpen: boolean
  row: DemandProcurementMasterRow
  onClose: () => void
}

/**
 * 需要調達計画マスタ 編集モーダル。
 *
 * useGetDemandProcurementMaster で full entity (codes / bgs 含む) を取得して
 * form を populate する。
 * bgCode / bgMasterId は登録後変更不可のため readonly で表示する。
 * note のみ編集可。codes / bgs は更新時も既存値をそのまま渡す。
 */
export const EditDemandProcurementPlanMasterModal = ({
  isOpen,
  row,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useUpdateDemandProcurementMaster()
  const {
    data,
    isPending: isFetching,
    isError,
    error,
  } = useGetDemandProcurementMaster(row.id)
  // 集約 RPC: 小売/自己託送区分でフィルタ済みの bgs を取得 (区分 client filter 不要)
  const { data: formOptions } = useGetDemandProcurementPlanMasterFormOptions()
  // codes section 用: 全件フェッチ (BG7 スコープ外、既存 hook 維持)
  const { data: allBgMastersData } = useListBgMasters()
  const { data: submitterData } = useListSubmitterMasters()
  const { data: tradingPartnersData } = useListTradingPartners()

  // 集約 RPC の bgs (小売/自己託送区分フィルタ済み): 所属BG section + area 解決用
  const bgMasters = useMemo(
    () =>
      formOptions !== undefined
        ? convertBgMasterListItems(formOptions.bgs)
        : [],
    [formOptions],
  )
  // codes section 用: 全件 BG (BG7 スコープ外、既存 fetch 維持)
  const allBgMasters = useMemo(
    () =>
      allBgMastersData !== undefined
        ? convertListBgMastersResponse(allBgMastersData)
        : [],
    [allBgMastersData],
  )
  // list クエリ取得中は undefined (fallback 解決の「(削除済み)」誤表示防止、
  // PR #5920 レビュー指摘)。
  const submitters = useMemo(
    () =>
      submitterData !== undefined
        ? convertListSubmitterMastersResponse(submitterData)
        : undefined,
    [submitterData],
  )
  const tradingPartners = useMemo(
    () => tradingPartnersData?.items ?? [],
    [tradingPartnersData],
  )

  const fetched = data?.demandProcurementMaster

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave: hookHandleSave,
    reset,
    setError,
    getValues,
    isDirty,
  } = useCrudModalForm<DemandProcurementMasterFormValues>({
    schema: demandProcurementMasterFormSchema,
    defaultValues: {
      bgMasterId: '',
      bgCode: '',
      note: '',
      codes: [],
      bgs: [],
      validFrom: todayCivilDate(),
      validTo: undefined,
    },
    onSubmit: async (values) => {
      if (fetched?.updatedAt === undefined) {
        throw new Error(
          t(
            'page:adminOcctoMaster.demandProcurementPlanMaster.serverError.unknown',
          ),
        )
      }
      await mutateAsync({
        id: row.id,
        note:
          values.note !== undefined && values.note !== ''
            ? values.note
            : undefined,
        codes: values.codes.map((c) => ({
          code: c.code,
          codeName: c.codeName,
          isInternal: c.isInternal,
          validFrom: c.validFrom,
          validTo: c.validTo,
        })),
        // 需要調達計画マスタは 1 record = 1 BG (= 代表BG) の設計 (2026-08-05
        // UX指示: 所属BG UI 削除)。 Edit では bgMasterId が immutable なので、
        // fetched.bgMasterId (= 元の代表BG) を常に送出する。
        bgs: [{ bgMasterId: fetched.bgMasterId }],
        validFrom: values.validFrom,
        validTo: values.validTo,
        expectedUpdatedAt: fetched.updatedAt,
      })
    },
    onClose,
    customErrorKeys: {
      [Code.Aborted]:
        'page:adminOcctoMaster.demandProcurementPlanMaster.serverError.aborted',
      [Code.NotFound]:
        'page:adminOcctoMaster.demandProcurementPlanMaster.serverError.notFound',
      [Code.InvalidArgument]:
        'page:adminOcctoMaster.demandProcurementPlanMaster.serverError.invalidArgument',
    },
    unknownErrorKey:
      'page:adminOcctoMaster.demandProcurementPlanMaster.serverError.unknown',
    // Update は代表BG (bg_master_id) を再検証しないため REP_BG_NOT_FOUND /
    // REP_BG_LICENSE_MISMATCH / duplicate は到達しない (現状は既存の
    // code fallback に委ねられるだけ)。他の master modal と同じく Create と
    // 同じ resolver を配線しておく。
    resolveDetailedError: resolveDemandProcurementMasterDetailedError,
  })

  // 他社コード候補の area 絞り込み用: bgCode と一致する BG マスタの areaCode を解決
  const bgAreaCode = useMemo(() => {
    const code = fetched?.bgCode
    if (code === undefined || code === '') return undefined
    return bgMasters.find((bg) => bg.bgCode === code)?.areaCode
  }, [fetched?.bgCode, bgMasters])
  useEffect(() => {
    if (fetched !== undefined) {
      reset({
        bgMasterId: fetched.bgMasterId,
        bgCode: fetched.bgCode,
        note: fetched.note ?? '',
        codes: fetched.codes.map((c) => ({
          code: c.code,
          codeName: c.codeName,
          isInternal: c.isInternal,
          validFrom:
            c.validFrom !== undefined
              ? {
                  year: c.validFrom.year,
                  month: c.validFrom.month,
                  day: c.validFrom.day,
                }
              : todayCivilDate(),
          validTo:
            c.validTo !== undefined
              ? {
                  year: c.validTo.year,
                  month: c.validTo.month,
                  day: c.validTo.day,
                }
              : undefined,
        })),
        bgs: fetched.bgs.map((b) => ({
          bgMasterId: b.bgMasterId,
        })),
        // AC-05: 保存済みの ValidFrom (適用開始日、編集可能) / ValidTo を表示する。
        validFrom:
          fetched.validFrom !== undefined
            ? {
                year: fetched.validFrom.year,
                month: fetched.validFrom.month,
                day: fetched.validFrom.day,
              }
            : todayCivilDate(),
        validTo:
          fetched.validTo !== undefined
            ? {
                year: fetched.validTo.year,
                month: fetched.validTo.month,
                day: fetched.validTo.day,
              }
            : undefined,
      })
    }
  }, [fetched, reset])

  // 代表BG (immutable、 fetched.bgMasterId) の有効期間チェックを handleSave の
  // pre-check として挟む。 所属BG UI は削除済みで bgMasterId 自体は編集不可だが、
  // ユーザーが validFrom を BG の有効期間外に変更して save すると期間外の代表BG
  // を含む Update request が送出され、 backend は Update で代表BG の再検証を
  // 行わないため invalid state が永続化される (PR #4772 review 指摘)。 fetched.
  // bgMasterId を bgMasters (集約 RPC の区分フィルタ済み) と allBgMasters
  // (全件、 fallback) から検索して isActiveOn で判定する。
  const validateRepBgPeriodBeforeSubmit = (): boolean => {
    if (fetched === undefined) return true
    const values = getValues()
    if (values.validFrom === undefined) return true
    const repBg =
      bgMasters.find((b) => b.id === fetched.bgMasterId) ??
      allBgMasters.find((b) => b.id === fetched.bgMasterId)
    // 判定源が見つからない (allBgMastersData 未取得 等) 場合は fail-open で
    // backend に委ねる (Create modal と同じ方針)。
    if (repBg === undefined) return true
    if (isActiveOn(repBg.validFrom, repBg.validTo, values.validFrom))
      return true
    setError('validFrom', {
      message: t(
        'page:adminOcctoMaster.demandProcurementPlanMaster.form.bgMasterOutOfPeriod',
      ),
    })
    return false
  }

  const handleSave = (): void => {
    if (!validateRepBgPeriodBeforeSubmit()) return
    hookHandleSave()
  }

  // AC-01: codes[] 配列全体の min(1) 違反 (field-level 表示)。
  const codesFieldError = errors.codes as
    | { root?: { message?: string }; message?: string }
    | undefined
  const codesErrorMessage =
    codesFieldError?.root?.message ??
    (typeof codesFieldError?.message === 'string'
      ? codesFieldError.message
      : undefined)

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      isSaveDisabled={isFetching || isError}
      onClose={handleClose}
      serverError={serverError}
      // design (`design/admin/occto.html` #pm-edit-modal) は `.ecl-modal--lg`。
      size="lg"
      title={t(
        'page:adminOcctoMaster.demandProcurementPlanMaster.modal.editTitle',
      )}
    >
      {isError && (
        <Alert
          status="error"
          title={mapMasterGetError(
            t,
            error,
            'page:adminOcctoMaster.demandProcurementPlanMaster.serverError',
          )}
        />
      )}
      <RegionLoading isLoading={isFetching}>
        {isFetching ? null : (
          <DemandProcurementPlanMasterFormFields
            // list クエリ取得中は undefined を渡し、 PlanMasterCodesSection の
            // fallback 解決が実在参照を一瞬「(削除済み)」に誤表示するのを防ぐ
            // (PR #5920 レビュー指摘)。 submit block (validateRepBgPeriodBeforeSubmit)
            // が使う allBgMasters 変数自体はここでは変更しない
            // (常に `[]`-coalesced のまま、 fail-open 挙動を変えない)。
            allBgMasters={
              allBgMastersData === undefined ? undefined : allBgMasters
            }
            bgAreaCode={bgAreaCode}
            codesErrorMessage={codesErrorMessage}
            control={control}
            errors={errors}
            isEdit
            selfBgCode={fetched?.bgCode}
            submitters={submitters}
            tradingPartners={tradingPartners}
          />
        )}
      </RegionLoading>
    </CrudModalLayout>
  )
}
